import type { GmRewardAction } from '@/components/features/gamification/model/types/gm-statuses';
import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { useUnclaimedGamificationRewardsCount } from '@/components/features/gamification/modules/rewards/hooks/useUnclaimedGamificationRewardsCount';
import Toast from '@/components/ui/Toast/toast';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { useProgressRouterIntel } from '@/hooks/useProgressRouter';
import openModal from '@/modals/utils/openModal';
import { useUserMeQuery } from '@/queries/user/user.queries';
import { getStatusUpRewardStatusId, getUnclaimedStatusUpRewards } from '../utils/statusUpRewards';
import { useManageStatusUpActions } from './useManageStatusUpActions';

const GAMIFICATION_REWARD_ACTION_TARGETS = {
  'level-up-card': {
    mobilePath: '/gamification/level-up',
    modalName: 'gm-card-level-up',
  },
  'status-up-card': {
    mobilePath: '/gamification/status-up',
    modalName: 'gm-card-statuses',
  },
  'gm-reward-redeem-instruction': {
    modalName: 'gm-reward-redeem-instruction',
  },
} as const satisfies Record<Exclude<GmRewardAction, 'open-support'>, {
  mobilePath?: string;
  modalName: Parameters<typeof openModal>[0];
}>;

export const useOpenGamificationRewardAction = () => {
  const t = useTranslations('gamification.scratch-card-statuses');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const queryClient = useQueryClient();
  const router = useProgressRouterIntel();
  const { data: user } = useUserMeQuery();
  // Grouped rewards are the lightweight source of truth for badges and for deciding
  // whether the reward should enter the claim flow or just show the cards.
  const { levelUpRewards, statusUpRewards } = useUnclaimedGamificationRewardsCount();
  const { handleScratchAction: handleLevelUpScratchAction } = useManageStatusUpActions('level-up');

  return useCallback(async (action?: GmRewardAction) => {
    if (!action) {
      return;
    }

    if (action === 'open-support') {
      showIntercom();
      return;
    }

    if (!user?.id) {
      return;
    }

    const target = GAMIFICATION_REWARD_ACTION_TARGETS[action];

    // Status-up has two flows:
    // 1. Unclaimed reward exists: show congratulations first, then its CTA continues to scratch-card claim.
    // 2. No unclaimed reward: keep the original statuses slider/card view as an informational fallback.
    if (action === 'status-up-card' && statusUpRewards > 0) {
      let rewards: Awaited<ReturnType<typeof getUnclaimedStatusUpRewards>>;
      try {
        rewards = await getUnclaimedStatusUpRewards(queryClient);
      } catch {
        Toast({
          type: 'error',
          title: t('rewards-load-error-title'),
          description: t('rewards-load-error-description'),
        });
        return;
      }
      const statusId = rewards[0] && getStatusUpRewardStatusId(rewards[0]);
      if (statusId) {
        openModal('gm-congratulations-status-up', { statusId });
        return;
      }
    }

    if (action === 'level-up-card' && levelUpRewards > 0) {
      handleLevelUpScratchAction();
      return;
    }

    if (isMobile && 'mobilePath' in target && target.mobilePath) {
      router.push(`${target.mobilePath}?userId=${user.id}`);
      return;
    }

    openModal(target.modalName);
  }, [handleLevelUpScratchAction, isMobile, levelUpRewards, queryClient, router, statusUpRewards, t, user?.id]);
};
